import React, { useState } from 'react';
import { CheckCircle, XCircle, Info } from '@phosphor-icons/react';
import { SingleConditionResultAudit } from 'shared/types/due-diligence';

interface ConditionResultRowProps {
  result: SingleConditionResultAudit;
  depth?: number;
}

/**
 * Renders a single condition result for the audit trail.
 * Supports nested children for condition groups.
 */
export const ConditionResultRow: React.FC<ConditionResultRowProps> = ({ result, depth = 0 }) => {
  const [showAiDetails, setShowAiDetails] = useState(false);
  const paddingLeft = depth * 16;

  return (
    <div style={{ paddingLeft }} className="py-1">
      <div className="flex items-start gap-2">
        {result.passed ? (
          <CheckCircle weight="fill" size={14} className="text-status-good mt-0.5 flex-shrink-0" />
        ) : (
          <XCircle weight="fill" size={14} className="text-status-bad mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neutral-secondary-dark">{result.description}</p>
          {result.details?.message && (
            <p className="text-xs text-neutral-secondary-mid mt-0.5">{result.details.message}</p>
          )}
          {result.details?.foundValue && (
            <p className="text-xs text-neutral-secondary-mid mt-0.5">
              Gevonden: <span className="font-mono">{result.details.foundValue}</span>
            </p>
          )}
          {/* AI Details toggle */}
          {result.aiDetails && (
            <div className="mt-1">
              <button
                onClick={(e) => { e.stopPropagation(); setShowAiDetails(!showAiDetails); }}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                <Info size={12} />
                {showAiDetails ? 'Verberg AI details' : 'Toon AI details'}
              </button>
              {showAiDetails && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs space-y-2">
                  <div>
                    <p className="font-medium text-blue-800">Prompt:</p>
                    <pre className="whitespace-pre-wrap text-blue-700 font-mono text-[10px] mt-1">{result.aiDetails.promptUsed}</pre>
                  </div>
                  <div>
                    <p className="font-medium text-blue-800">AI Antwoord ({result.aiDetails.model}):</p>
                    <pre className="whitespace-pre-wrap text-blue-700 font-mono text-[10px] mt-1">{result.aiDetails.rawResponse}</pre>
                  </div>
                  {result.aiDetails.interpretedAs && (
                    <p className="text-blue-800">
                      Geïnterpreteerd als: <span className="font-medium">{result.aiDetails.interpretedAs}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Nested children for groups */}
      {result.children && result.children.length > 0 && (
        <div className="mt-1 border-l-2 border-neutral-primary/20 ml-1.5">
          {result.children.map((child, idx) => (
            <ConditionResultRow key={child.conditionId || idx} result={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

interface OptionEvaluation {
  optionId: number;
  optionLabel: string;
  matched: boolean;
  conditionResults?: SingleConditionResultAudit[];
}

interface DueDiligenceAuditTrailProps {
  conditionEvaluations: {
    optionEvaluations?: OptionEvaluation[];
    summary?: string;
  };
}

/**
 * Component to display the condition evaluation audit trail.
 * Shows how AI evaluated each answer option based on conditions.
 */
const DueDiligenceAuditTrail: React.FC<DueDiligenceAuditTrailProps> = ({
  conditionEvaluations,
}) => {
  if (!conditionEvaluations?.optionEvaluations?.length) {
    return null;
  }

  return (
    <div className="bg-neutral-100 rounded-lg p-3 space-y-3">
      {conditionEvaluations.summary && (
        <p className="text-xs text-neutral-secondary-dark font-medium">
          {conditionEvaluations.summary}
        </p>
      )}
      {conditionEvaluations.optionEvaluations.map((optionEval) => (
        <div key={optionEval.optionId} className="bg-white rounded p-2">
          <div className="flex items-center gap-2 mb-1">
            {optionEval.matched ? (
              <CheckCircle weight="fill" size={14} className="text-status-good" />
            ) : (
              <XCircle weight="fill" size={14} className="text-neutral-secondary-mid" />
            )}
            <span className={`text-xs font-medium ${optionEval.matched ? 'text-status-good' : 'text-neutral-secondary-mid'}`}>
              {optionEval.optionLabel}
            </span>
            {optionEval.matched && (
              <span className="text-xs bg-status-good/10 text-status-good px-1.5 py-0.5 rounded">
                Geselecteerd
              </span>
            )}
          </div>
          {optionEval.conditionResults?.map((result, idx) => (
            <ConditionResultRow key={result.conditionId || idx} result={result} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default DueDiligenceAuditTrail;
